// ============================================================================
// Lightweight i18n system — no external dependency.
// Supported: en (default), fi, sv, no, da, pl, it, fr.
// Full translation dictionary lives in ./i18nTranslations.
// ============================================================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { TRANSLATIONS } from "./i18nTranslations";

// `numberLocale` drives Intl.NumberFormat. `pdfSafe` is false only for pl —
// its Latin Extended-A diacritics must be transliterated before rendering
// into a jsPDF Helvetica document.
// `defaultCurrency` is the currency automatically selected when the user
// picks that language (each of Helvar's main markets has a natural default).
export const LOCALES = [
  { code: "en", label: "English",   numberLocale: "en-GB", pdfSafe: true, defaultCurrency: null },
  { code: "da", label: "Dansk",     numberLocale: "da-DK", pdfSafe: true, defaultCurrency: "DKK" },
  { code: "fr", label: "Français",  numberLocale: "fr-FR", pdfSafe: true, defaultCurrency: "EUR" },
  { code: "it", label: "Italiano",  numberLocale: "it-IT", pdfSafe: true, defaultCurrency: "EUR" },
  { code: "nl", label: "Nederlands", numberLocale: "nl-NL", pdfSafe: true, defaultCurrency: "EUR" },
  { code: "no", label: "Norsk",     numberLocale: "nb-NO", pdfSafe: true, defaultCurrency: "NOK" },
  { code: "pl", label: "Polski",    numberLocale: "pl-PL", pdfSafe: false, defaultCurrency: "PLN" },
  { code: "fi", label: "Suomi",     numberLocale: "fi-FI", pdfSafe: true, defaultCurrency: "EUR" },
  { code: "sv", label: "Svenska",   numberLocale: "sv-SE", pdfSafe: true, defaultCurrency: "SEK" },
];

const DEFAULT_LOCALE = "en";

// ---------- Locale-aware formatters --------------------------------------
export function formatNumber(value, locale, opts = {}) {
  if (!Number.isFinite(value)) value = 0;
  try {
    return new Intl.NumberFormat(locale, opts).format(value);
  } catch (e) {
    return String(value);
  }
}

export function formatCurrency(value, symbol = "€", locale = "en-GB") {
  if (!Number.isFinite(value)) value = 0;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const formatted = formatNumber(abs, locale, { maximumFractionDigits: 0 });
  return `${sign}${symbol}${formatted}`;
}

export function formatKwh(value, locale = "en-GB") {
  if (!Number.isFinite(value)) value = 0;
  return formatNumber(Math.round(value), locale);
}

// ---------- PDF character sanitiser -------------------------------------
// jsPDF's built-in Helvetica font is encoded as WinAnsi (Windows-1252).
// Any glyph outside that range (Polish Latin-Extended-A, subscripts, etc.)
// renders as "?" or an empty box. We transliterate those specific chars
// to their closest ASCII/Latin-1 equivalent before rendering.
const PL_MAP = {
  "ą": "a", "Ą": "A", "ć": "c", "Ć": "C", "ę": "e", "Ę": "E",
  "ł": "l", "Ł": "L", "ń": "n", "Ń": "N", "ś": "s", "Ś": "S",
  "ź": "z", "Ź": "Z", "ż": "z", "Ż": "Z",
};
// Universal replacements applied to every locale — these Unicode chars
// appear in multiple translations (CO₂, en/em dashes, arrows, …) and
// none of them exist in WinAnsi.
const UNIVERSAL_MAP = {
  "\u2080": "0", "\u2081": "1", "\u2082": "2", "\u2083": "3", "\u2084": "4",
  "\u2085": "5", "\u2086": "6", "\u2087": "7", "\u2088": "8", "\u2089": "9",
  "\u2192": "->",  // → arrow (defensive)
  "\u2190": "<-",
  "\u00A0": " ",   // non-breaking space
  "\u202F": " ",   // narrow no-break space (fr-FR thousands separator)
};
export function sanitizeForPdf(str, locale) {
  if (typeof str !== "string") return "";
  // Universal transliteration — the jsPDF built-in Helvetica always uses
  // WinAnsi regardless of the current UI locale, so Polish diacritics
  // (present in the PLN currency symbol "zł", client names, etc.) must be
  // stripped even when the UI is not Polish.
  let out = str.replace(
    /[\u2080-\u2089\u2192\u2190\u00A0\u202F]/g,
    (ch) => UNIVERSAL_MAP[ch] || ch
  );
  out = out.replace(
    /[\u0104\u0105\u0106\u0107\u0118\u0119\u0141\u0142\u0143\u0144\u015A\u015B\u0179\u017A\u017B\u017C]/g,
    (ch) => PL_MAP[ch] || ch
  );
  // `locale` retained for future locale-specific transliteration hooks.
  void locale;
  return out;
}

// ---------- React context & provider --------------------------------------

const I18nContext = createContext(null);
const STORAGE_KEY = "helvar_locale";

export function I18nProvider({ children, onLocaleChange }) {
  const [locale, setLocaleState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LOCALE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return LOCALES.some((l) => l.code === stored) ? stored : DEFAULT_LOCALE;
  });

  const setLocale = useCallback((next) => {
    if (!LOCALES.some((l) => l.code === next)) return;
    setLocaleState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
    if (typeof onLocaleChange === "function") onLocaleChange(next);
  }, [onLocaleChange]);

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = locale;
  }, [locale]);

  const numberLocale = useMemo(
    () => LOCALES.find((l) => l.code === locale)?.numberLocale || "en-GB",
    [locale]
  );

  const t = useCallback(
    (key, vars) => {
      const dict = TRANSLATIONS[locale] || TRANSLATIONS.en;
      const raw = dict[key] || TRANSLATIONS.en[key] || key;
      if (!vars) return raw;
      return raw.replace(/\{\{(\w+)\}\}/g, (_, k) =>
        vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`
      );
    },
    [locale]
  );

  const value = useMemo(() => ({
    t,
    locale,
    setLocale,
    LOCALES,
    numberLocale,
    formatNumber: (v, opts) => formatNumber(v, numberLocale, opts),
    formatKwh: (v) => formatKwh(v, numberLocale),
    formatCurrency: (v, symbol) => formatCurrency(v, symbol, numberLocale),
    sanitizeForPdf: (s) => sanitizeForPdf(s, locale),
    isPdfSafe: LOCALES.find((l) => l.code === locale)?.pdfSafe ?? true,
  }), [t, locale, numberLocale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
